import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';
import { BasketActionTypes, BasketActions } from '../';
import { ErrorAction, AppState } from 'store';
import { ACTION_CREATOR_NAME, GtmCheckoutSteps, analyticsHelper, errorToBasketErrorModel, getCurrentLocation, externalComponentsHelper } from 'helpers';
import { updateSofaglideSaga } from './updateSofaglide';
import { loadBasketSaga } from './loadBasket';
import { validateBasketReservationsSaga } from './validation/validateBasketReservations';
import { validateCartSaga } from './validation/validateCart';
import { api } from 'repositories/api';
import { BasketItemModel } from 'models/Basket';
import { PaymentTypes } from 'models/Payment';
import { BasketErrorModel } from 'models/Errors';
import { initializePaymentCalculatorSaga } from '../../payment/actions';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { saveCurrentPageSaga } from './saveCurrentPage';

const RESERVATION_ERROR_MESSAGE = 'Reservation has expired (init basket)';

export interface InitBasketPageAction extends Action<BasketActionTypes.INIT_BASKET_PAGE> {
    isFirstPageLoading?: boolean;
}

export interface InitBasketPageActionStart extends Action<BasketActionTypes.INIT_BASKET_PAGE_START> {
}

export interface InitBasketPageActionSuccess extends Action<BasketActionTypes.INIT_BASKET_PAGE_SUCCESS> {
}

export interface InitBasketPageActionError extends ErrorAction<BasketActionTypes.INIT_BASKET_PAGE_ERROR> {
}

export const initBasketPage: (isFirstPageLoading: boolean) => InitBasketPageAction = (isFirstPageLoading: boolean) => ({
    type: BasketActionTypes.INIT_BASKET_PAGE,
    isFirstPageLoading
});
initBasketPage[ACTION_CREATOR_NAME] = 'initBasketPage';

export type InitBasketPageFunc = (...args: Parameters<typeof initBasketPage>) => void;

export function* initBasketPageSaga({ isFirstPageLoading }: InitBasketPageAction): BasketSagaReturnType {
    yield put<BasketActions>({
        type: BasketActionTypes.INIT_BASKET_PAGE_START,
    });

    try {
        yield callBasketSaga(loadBasketSaga);

        let state: AppState = yield select();
        const basket = state.basketState.basket;

        if (basket) {
            externalComponentsHelper.updateTopBasketItems(basket.totalBasketItemsCount);
        }

        if (basket && !basket.isEmpty()) {
            /** synchronous blocking call for update sofaglide before others actions */
            yield callBasketSaga(updateSofaglideSaga);

            yield put<BasketActions>({
                type: BasketActionTypes.REFRESH_AVAILABLE_UPSELLS
            });

            yield callBasketSaga(initializePaymentCalculatorSaga);

            yield callBasketSaga(validateBasketReservationsSaga, {
                type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS,
                isBasketPage: true
            });
        }

        if (isFirstPageLoading) {
            yield callBasketSaga(saveCurrentPageSaga, {
                type: BasketActionTypes.SAVE_CURRENT_PAGE,
                currentPage: getCurrentLocation()
            });
        }

        if (isFirstPageLoading && basket) {
            sendAnalytics(basket.guid, basket.entries, basket.leadTime, state.paymentState.paymentDetails.paymentType, basket.user.uid);
        }

        state = yield select();

        if (state.basketState.shouldShowReservationWarning) {
            throw new BasketErrorModel(RESERVATION_ERROR_MESSAGE, undefined, 'initBasketPageSaga');
        }

        yield put<BasketActions>({
            type: BasketActionTypes.INIT_BASKET_PAGE_SUCCESS,
        });

        yield callBasketSaga(validateCartSaga, {
            type: BasketActionTypes.VALIDATE_CART,
            isBasketPage: true
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.INIT_BASKET_PAGE_ERROR,
            error: errorToBasketErrorModel(error, 'initBasketPageSaga')
        });

        return false;
    }
}

function sendAnalytics(basketId: string, entries: BasketItemModel[], leadTime: number, paymentType: PaymentTypes, userId: string): void {
    analyticsHelper.withLogging(() => {
        api.google.checkoutStep(GtmCheckoutSteps.Basket, basketId, entries, leadTime, paymentType, userId);
    }, 'initBasketPageSaga');
}